import type { InitCheckoutResponse } from '../../checkout-be/src/schemas/initCheckout'
import { initCheckout } from './contractService'
import {
    addBlurListeners,
    addSubmitListener,
    CheckoutEvent,
    dispatchEvent,
    postEvent,
} from './events'
import {
    injectCSSandScripts,
    injectHTML,
    injectLoadingScreen,
    showErrorScreen,
} from './injectHtml'
import { initializeTabs } from './paymentTabs'
import { initialisePaysafe } from './paysafe'
import { clearErrorMessages, validationInit } from './validation'
import type {
    DngCheckout,
    InitOptions,
    LoadOptions,
    PaymentOption,
} from '../../checkout-sdk-types/types'
import type { VersionInfo } from './versionConfig'
import { VersionConfig } from './versionConfig'
import type { PaysafeInstance } from '../models/paysafe'
import { v7 as uuidv7 } from 'uuid'

declare const INIT_CHECKOUT_URL: string
declare const CREATE_CONTRACT_SDK_URL: string
declare const GET_CONTRACT_STATUS_URL: string
declare const SDK_DISTRIBUTION_URL: string
declare const POST_EVENT_URL: string

export class CheckoutHandler {
    public static readonly initCheckoutUrl = INIT_CHECKOUT_URL
    public static readonly createContractSdkUrl = CREATE_CONTRACT_SDK_URL
    public static readonly contractStatusUrl = GET_CONTRACT_STATUS_URL
    public static readonly sdkDistributionUrl = SDK_DISTRIBUTION_URL
    public static readonly postEventUrl = POST_EVENT_URL
    public authToken: InitOptions['authToken']
    public basket: InitOptions['basket']
    public customer: InitOptions['customer']
    public initCheckoutResponseData: InitCheckoutResponse
    public paymentOption: PaymentOption
    public paysafeIframeToken: string
    public paysafeInstance: PaysafeInstance | undefined
    public targetElementId: string
    public basketTotal: number
    public achBasketTotal: number
    public achNetFirstPaymentAmountAfterPromotionalPeriod: number
    public formSubmitted: boolean
    public creatingContract: boolean
    public transactionId: string

    constructor(options: InitOptions) {
        this.authToken = options.authToken
        this.basket = options.basket
        this.customer = options.customer
        this.initCheckoutResponseData = {} as InitCheckoutResponse
        this.paymentOption = 'ach'
        this.paysafeIframeToken = ''
        this.targetElementId = ''
        this.basketTotal = 0
        this.achBasketTotal = 0
        this.achNetFirstPaymentAmountAfterPromotionalPeriod = 0
        this.formSubmitted = false
        this.creatingContract = false
        this.transactionId = uuidv7()
    }

    public static initialise: DngCheckout['initialise'] = async (
        targetElementId: string,
        options: InitOptions,
    ): Promise<{ renderCheckout: () => Promise<void> }> => {
        console.debug('Initialise called..')
        injectCSSandScripts()

        const validOptions: InitOptions = validationInit(options)

        console.debug('Response recieved from init checkout backend')
        const checkoutInstance = new CheckoutHandler(validOptions)

        try {
            console.debug('Calling init checkout backend')
            checkoutInstance.initCheckoutResponseData = await initCheckout(
                validOptions.authToken,
                {
                    transactionId: checkoutInstance.transactionId,
                    channelSourceData: validOptions.basket.channelSourceData,
                },
            )
            console.debug('Successfully called init checkout backend')
            console.debug(
                'initCheckoutResponseData',
                JSON.stringify(checkoutInstance.initCheckoutResponseData),
            )
            checkoutInstance.targetElementId = targetElementId
            checkoutInstance.paysafeIframeToken =
                checkoutInstance.initCheckoutResponseData.credentials.paysafeIframeToken
        } catch (error) {
            console.error('Error initialising checkout', error)
            const eventDescription = 'Failed to initialise checkout'
            dispatchEvent(CheckoutEvent.FAILURE, {
                status: 'error',
                eventDescription,
            })

            postEvent(checkoutInstance.authToken, {
                eventName: CheckoutEvent.FAILURE,
                transactionId: '',
                source: 'checkout_sdk',
                additionalProps: {
                    status: 'error',
                    eventDescription,
                },
                checkoutSourceRef:
                    checkoutInstance.basket.channelSourceData.checkoutSourceRef,
            })
            showErrorScreen(checkoutInstance)
        }

        return new Promise((resolve) =>
            resolve({
                renderCheckout: () =>
                    checkoutInstance.renderCheckout(
                        targetElementId,
                        checkoutInstance,
                    ),
            }),
        )
    }

    public setPaymentOption(paymentOption: PaymentOption): void {
        this.paymentOption = paymentOption

        // Reset error messages when switching tabs
        clearErrorMessages()

        if (paymentOption === 'card') {
            this.paysafeInstance?.show()
        }
    }

    public async renderCheckout(
        targetElementId: string,
        checkoutInstance: CheckoutHandler,
    ): Promise<void> {
        await injectHTML(targetElementId, checkoutInstance)

        initializeTabs(checkoutInstance)
        addSubmitListener(checkoutInstance)
        addBlurListeners(checkoutInstance)
        await initialisePaysafe(checkoutInstance)
    }

    // Load and render the checkout in a specific target element by its ID
    public static loadCheckout: DngCheckout['loadCheckout'] = async (
        options: LoadOptions,
    ): Promise<void> => {
        const targetElement = document.getElementById(options.targetElementId)
        if (targetElement) {
            console.info(
                'Loading checkout for user with token on target element',
                options.targetElementId,
            )
            injectLoadingScreen(options.targetElementId)

            const res = await CheckoutHandler.initialise(
                options.targetElementId,
                options,
            )
            await res.renderCheckout()
        } else {
            console.error(
                `Element with id ${options.targetElementId} not found`,
            )
        }
    }
}

export const setupCheckout = (): void => {
    window.dngCheckout = {
        initialise: CheckoutHandler.initialise,
        loadCheckout: CheckoutHandler.loadCheckout,
        getMetadata: (): VersionInfo => VersionConfig,
    }
}
